/**
 * When a chef printout's counts lock (PROD-1452).
 *
 * Schools change quantities until their own order deadline, so a packet
 * printed before it is out of date and a chef who cooks from it shorts the
 * school. A printed day is still open while ANY school on it can change its
 * order:
 *   - every shipment on the day, against its school's cutoff for the
 *     shipment's SERVICE date (the day the school orders for), and
 *   - every school the kitchen serves that has no order on the day yet (it
 *     can still place one), unless the day is a no-service day for it.
 * The printout locks when the LAST of those windows closes; each page carries
 * the watermark only while a window it counts is open (`printPreliminary`).
 */

import { openPrintWindows, printLocksAt as sharedPrintLocksAt, type PrintWindow } from '@ordo/recurrence'

import type {
  KitchenServedSchoolDeadline,
  KitchenServedSchoolNoServiceDay,
  KitchenShipmentSummary,
} from '@/api/kitchen-orders'
import { campusBlockId, type PrintPreliminary } from '@/lib/chefs/print-plan'

// The rule itself lives in `@ordo/recurrence` (print-lock.ts) so the server's
// "orders ready" send (PROD-1503) judges a week exactly as this dialog does.
// This module only pins the client's API types onto it.

export interface PrintLockInput {
  /** Printed days (`YYYY-MM-DD`, DELIVERY axis — the chef surfaces' day). */
  dayKeys: Iterable<string>
  shipments: ReadonlyArray<KitchenShipmentSummary>
  servedSchools: ReadonlyArray<KitchenServedSchoolDeadline>
  noServiceDays?: ReadonlyArray<KitchenServedSchoolNoServiceDay>
  now: Date
}

/** When the printed days' counts become final, or null when they already are. */
export function printLocksAt(input: PrintLockInput): Date | null {
  return sharedPrintLocksAt(input)
}

function latest(windows: ReadonlyArray<PrintWindow>): Date | null {
  let at: Date | null = null
  for (const w of windows) {
    if (at === null || w.closesAt.getTime() > at.getTime()) {
      at = w.closesAt
    }
  }
  return at
}

/** When the next window on the printed days closes — the moment some page of the packet stops being preliminary. */
export function nextPrintLockAt(input: PrintLockInput): Date | null {
  let at: Date | null = null
  for (const w of openPrintWindows(input)) {
    if (at === null || w.closesAt.getTime() < at.getTime()) {
      at = w.closesAt
    }
  }
  return at
}

/**
 * The packet's lock labels per page scope, or undefined when every count is
 * final. A school's order sheet is preliminary only while THAT school can
 * still change its order; a page that totals a day (deliverables, recipes)
 * while any school on the day can; the cover and ingredient list while any
 * school in the packet can.
 */
export function printPreliminary(input: PrintLockInput): PrintPreliminary | undefined {
  const windows = openPrintWindows(input)
  const packet = latest(windows)
  if (!packet) {
    return undefined
  }
  const label = (keyOf: (w: PrintWindow) => string): Record<string, string> => {
    const latestByKey = new Map<string, Date>()
    for (const w of windows) {
      const prior = latestByKey.get(keyOf(w))
      if (!prior || w.closesAt.getTime() > prior.getTime()) {
        latestByKey.set(keyOf(w), w.closesAt)
      }
    }
    return Object.fromEntries([...latestByKey].map(([key, at]) => [key, formatPrintLock(at)]))
  }
  return {
    locksAtLabel: formatPrintLock(packet),
    dayLocksAtLabel: label((w) => w.dayKey),
    campusLocksAtLabel: label((w) => campusBlockId(w.dayKey, w.schoolLocationId)),
  }
}

/**
 * "Fri, Oct 9, 11:59 PM EDT" — the lock instant as printed on the page.
 * Assembled from parts: `toLocaleString`'s joining text depends on the ICU
 * version (newer ones write "Oct 9 at 11:59 PM"), so the same instant
 * printed differently across runtimes.
 */
export function formatPrintLock(at: Date, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone,
  }).formatToParts(at)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  return `${part('weekday')}, ${part('month')} ${part('day')}, ${part('hour')}:${part('minute')} ${part('dayPeriod')} ${part('timeZoneName')}`
}

/**
 * PROD-1503 — the chef "orders ready" email and SMS link to
 * `/orders/kitchen?date=<Monday>&view=weekly&print=1`. `print=1` asks the
 * Orders page to open Print Orders once the week's orders have loaded.
 */
export const PRINT_ON_LOAD_PARAM = 'print'

/** Whether the URL asks for the print dialog on load. */
export function wantsPrintOnLoad(params: URLSearchParams): boolean {
  return params.get(PRINT_ON_LOAD_PARAM) === '1'
}

/** The same params without the print request, so a reload or shared link does not reopen the dialog. */
export function withoutPrintOnLoad(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete(PRINT_ON_LOAD_PARAM)
  return next
}
